<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
      <!-- Page header: title left, actions right (same pattern as the CAGISTA MDM hub) -->
      <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:1rem">
        <div>
          <button class="mdm-back" @click="$router.push('/mdm')">
            <font-awesome-icon :icon="['fas', 'arrow-left']" /> CAGISTA MDM
          </button>
          <h3 class="mb-1 mt-1">Apper</h3>
          <div style="font-style: italic; color:var(--bo-muted); max-width:680px">
            Distribusjon av CAGISTA-appene (TastyPOS/TastyKIOSK) til enheter via managed Google Play —
            <b>Google-hostede private apper</b>: APK-en lastes opp til Play, og enhetene laster ned fra
            Play. S3 er vårt arkiv og revisjonsspor — <b>ikke</b> et rulle-tilbake-lager: en arkivert APK kan
            aldri installeres på en administrert enhet. Se <code>docs/app-distribution-runbook.md</code>.
          </div>
          <!-- «Aktiver» publiserer releasen i produksjonens Play Console og ruller den til hele flåten.
               Play-utgiveren er delt mellom miljøene, akkurat som AMAPI-enterprisen. -->
          <div v-if="isLocalDev()" class="mdm-devbanner mt-2">
            <font-awesome-icon :icon="['fas', 'lock']" />
            <span>
              Kjører fra <code>localhost</code> mot samme Play-utgiver og AMAPI-enterprise som produksjon.
              Siden er <strong>skrivebeskyttet</strong> her: versjoner kan leses, men ikke lastes opp,
              aktiveres eller slettes, og app-distribusjonen kan ikke endres. «Aktiver» publiserer releasen
              i Play og ruller den til hele flåten — <strong>gjør releaser fra produksjons-Backoffice</strong>.
            </span>
          </div>
        </div>
        <div class="d-flex flex-column align-items-end" style="gap:0.5rem; margin-top:1.6rem">
          <div class="d-flex align-items-center" style="gap:0.6rem">
            <span v-if="storedIn" class="apps-meta">Lagring <code>{{ storedIn }}</code></span>
            <span class="badge" :class="apkForm.publisherConfigured ? 'bg-success' : 'bg-secondary'"
                  style="font-weight:500"
                  title="Med Google-hosting laster «Aktiver» APK-en opp til Play Console. Uten publisher-service-konto blir aktivering kun lokal, og Play får ingenting.">
              {{ apkForm.publisherConfigured ? 'Play-publisering ✓' : 'Play-publisering mangler' }}
            </span>
          </div>
          <div class="d-flex gap-2">
            <button class="bo_btn_secondary" @click="openConfigModal">
              <font-awesome-icon :icon="['fas', 'cog']" /> App-distribusjon
            </button>
            <button class="bo_btn" @click="openUploadModal" :disabled="isLocalDev()"
                    :title="isLocalDev() ? 'Skrivebeskyttet fra localhost — bruk produksjons-Backoffice' : ''">
              <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" /> Last opp APK
            </button>
          </div>
        </div>
      </div>

      <!-- ===== Hosted APKs (overview) ===== -->
      <div class="cfg-card">
        <div class="cfg-card-header">
          <span class="cfg-icon"><font-awesome-icon :icon="['fas', 'archive']" /></span>
          <h5>Opplastede APK-er</h5>
          <span class="badge bg-secondary">{{ apks.length }}</span>
          <button class="apk-refresh ms-auto" title="Oppdater listen" @click="loadApks" :disabled="apksLoading">
            <font-awesome-icon :icon="['fas', 'sync']" :class="{ 'fa-spin': apksLoading }" />
          </button>
        </div>
        <div class="cfg-desc">
          Våre opplastede releaser. «Aktiver» laster den valgte opp til Play Console — det er derfra
          enhetene installerer. Her ligger arkivet og versjonshistorikken.
        </div>

        <p v-if="apksError" class="text-danger mt-2 mb-0">{{ apksError }}</p>
        <p v-if="apksNotice" class="text-success mt-2 mb-0">{{ apksNotice }}</p>
        <div v-if="!apksLoading && !apksError && apks.length === 0" class="apk-empty">
          <font-awesome-icon :icon="['fas', 'box-open']" class="apk-empty__icon" />
          <div class="mt-2" style="color:var(--bo-muted)">Ingen APK-er lastet opp ennå.</div>
          <button class="bo_btn mt-3" @click="openUploadModal" :disabled="isLocalDev()"
                  :title="isLocalDev() ? 'Skrivebeskyttet fra localhost — bruk produksjons-Backoffice' : ''">Last opp APK</button>
        </div>

        <div v-if="apks.length" class="table-responsive mt-2">
          <table class="table table-hover align-middle apk-table">
            <thead>
              <tr>
                <th></th>
                <th>App</th>
                <th>Pakkenavn</th>
                <th class="text-end">Version code</th>
                <th>Version name</th>
                <th class="text-end">Størrelse</th>
                <th>SHA-256 (base64)</th>
                <th>Lastet opp</th>
                <th class="text-end">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in apks" :key="a.packageName + ':' + a.versionCode">
                <td class="apk-col-delete">
                  <button v-if="!a.active" class="apk-icon-btn apk-icon-btn--danger" :disabled="isLocalDev()"
                          :title="isLocalDev() ? 'Skrivebeskyttet fra localhost' : 'Slett denne versjonen'"
                          @click="deleteApk(a)">
                    <font-awesome-icon :icon="['fas', 'times']" />
                  </button>
                  <span v-else class="apk-col-delete__lock" title="Den aktive versjonen kan ikke slettes — aktiver en annen først">
                    <font-awesome-icon :icon="['fas', 'lock']" />
                  </span>
                </td>
                <td class="fw-semibold text-nowrap">{{ appName(a) }}</td>
                <td><code class="apk-pkg">{{ a.packageName }}</code></td>
                <td class="text-end">{{ a.versionCode || '—' }}</td>
                <td class="text-nowrap">{{ a.versionName || '—' }}</td>
                <td class="text-end text-nowrap">{{ formatBytes(a.bytes) }}</td>
                <td>
                  <code v-if="a.sha256Base64" class="apk-sha" :title="a.sha256Base64 + ' — klikk for å kopiere'"
                        @click="copySha(a)">
                    {{ copiedPkg === a.packageName + ':' + a.versionCode ? 'Kopiert ✓' : shortSha(a.sha256Base64) }}
                  </code>
                  <span v-else style="color:#adb5bd" title="Lastet opp utenfor konsollen (f.eks. aws CLI) — ingen metadata.">—</span>
                </td>
                <td class="text-nowrap">{{ formatTime(a.lastModifiedMs) }}</td>
                <td class="text-end text-nowrap">
                  <button class="apk-icon-btn me-1" title="Definition-fil (LEGACY — kun for den utgåtte self-hosted-modellen; Google-hostet laster «Aktiver» opp APK-en selv)"
                          @click="downloadDefinition(a)" :disabled="definitionBusy === a.packageName + ':' + a.versionCode">
                    <font-awesome-icon :icon="['fas', 'file-download']"
                                       :class="{ 'fa-fade-busy': definitionBusy === a.packageName + ':' + a.versionCode }" />
                  </button>
                  <button class="apk-icon-btn apk-activate me-1"
                          title="Er appen distribuert til vår enterprise i managed Google Play? (404 = ingen enhet kan installere den)"
                          @click="checkAvailability(a)" :disabled="availabilityBusy === a.packageName">
                    {{ availabilityBusy === a.packageName ? 'Sjekker…' : 'Managed Play' }}
                  </button>
                  <span v-if="a.active" class="badge bg-success" title="Denne versjonen er lastet opp til Play og er den enhetene installerer">Aktiv</span>
                  <button v-else class="apk-icon-btn apk-activate"
                          :title="isLocalDev() ? 'Skrivebeskyttet fra localhost — bruk produksjons-Backoffice'
                                               : 'Last denne versjonen opp til Play Console og gjør den gjeldende'"
                          @click="activateApk(a)"
                          :disabled="!!activateBusy">
                    <template v-if="activateBusy === a.packageName + ':' + a.versionCode">
                      <span class="apk-spinner" aria-hidden="true"></span>Laster opp til Play…
                    </template>
                    <template v-else>Aktiver</template>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="apks.length" class="apk-footnote">
          <strong>Aktiv</strong> = versjonen som er lastet opp til Play og gjelder nå.
          <b>Det finnes ingen rulle-tilbake:</b> Play nekter en lavere version code enn den som ligger ute,
          og en enhet kan aldri få servert en eldre build. En dårlig release fikses bare med en NY release —
          derfor skal du teste på én merchant først (Trinnvis utrulling). <font-awesome-icon :icon="['fas', 'file-download']" /> laster ned
          <strong>definition-filen</strong> — <em>legacy</em>, den hørte til den utgåtte self-hosted-modellen.
          Google-hostet trenger den ikke: «Aktiver» laster APK-en opp til Play. Version code skal matche
          <code>applicationReports</code> på enhetene etter utrulling.
        </div>
      </div>
      <app-rollout :packages="rolloutPackages" />
  </div>

  <ConfirmDialogue ref="confirmDialog" />

  <!-- ===== Upload modal ===== -->
  <popup-modal ref="uploadModal">
    <h5 class="mb-1">
      <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" style="color:#ECB109" />
      Last opp signert APK (release)
    </h5>
    <div class="cfg-desc" v-if="uploadPackageName">
      Lagres versjonert i <code>{{ (apkForm.s3KeyPrefix || 'apks/') + uploadPackageName + '/{versionCode}.apk' }}</code>
      i {{ apkForm.s3Bucket ? 'S3-bucketen «' + apkForm.s3Bucket + '»' : 'serverens lokale mappe' }} —
      hver release ligger for seg, og Aktiver-pekeren avgjør hvilken som er gjeldende. «Aktiver» laster
      deretter APK-en opp til Play Console — det er det enhetene installerer fra (se runbook §2).
    </div>

    <div class="mt-3">
      <label class="form-label mb-1 mdm-label">App (pakkenavn)</label>
      <select v-if="apkForm.apps.length" class="form-select" v-model="uploadPackageName">
        <option v-for="app in apkForm.apps" :key="app.packageName" :value="app.packageName">
          {{ (app.displayName || app.packageName) }} — {{ app.packageName }}
        </option>
      </select>
      <div v-else class="alert alert-warning py-2 mb-0" style="font-size:0.85rem">
        Ingen apper er konfigurert ennå. Legg til appen (pakkenavn) under
        <a href="#" @click.prevent="openConfigFromUpload">App-distribusjon</a> først.
      </div>
    </div>
    <div class="mt-2">
      <label class="form-label mb-1 mdm-label">Signert release-APK</label>
      <input class="form-control" type="file" accept=".apk" ref="apkFile" @change="onApkFileChange" />
    </div>

    <div class="mt-3 d-flex align-items-center gap-3">
      <button class="bo_btn" @click="uploadApk" :disabled="uploading || !uploadFile || !uploadPackageName">
        {{ uploading ? 'Laster opp… ' + uploadPct + '%' : 'Last opp APK' }}
      </button>
      <span v-if="uploadMessage" :class="uploadError ? 'text-danger' : 'text-success'">{{ uploadMessage }}</span>
    </div>
    <div v-if="uploading" class="upload-progress mt-2">
      <div :style="{ width: uploadPct + '%' }"></div>
    </div>

    <div v-if="uploadResult" class="alert alert-success mt-3 py-2" style="font-size:0.84rem">
      <div>
        <strong>{{ uploadResult.label || uploadResult.packageName }}</strong>
        <template v-if="uploadResult.versionName"> {{ uploadResult.versionName }} (version code {{ uploadResult.versionCode }})</template>
        — {{ formatBytes(uploadResult.bytes) }} lagret: <code>{{ uploadResult.storedAt }}</code>
      </div>
      <div class="mt-1">SHA-256 (base64): <code style="word-break:break-all">{{ uploadResult.sha256Base64 }}</code></div>
      <div class="mt-1">
        <span class="badge bg-secondary">Ikke aktiv ennå</span>
        <span style="color:var(--bo-muted)" class="ms-1">
          Opplasting aktiverer aldri noe. Aktiver den i listen når den skal rulles ut — fram til da
          installerer enhetene fortsatt versjonen som ligger i Play.
        </span>
      </div>
      <div style="color:var(--bo-muted)" class="mt-1">
        SHA-256-en er revisjonssporet: den identifiserer nøyaktig hvilken binær som ble lagret, og som
        «Aktiver» laster opp til Play. Den gamle definition-filen i Play Console trengs ikke lenger —
        self-hosted-modellen er utgått.
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <button class="bo_btn_secondary" @click="$refs.uploadModal.close()" :disabled="uploading">Lukk</button>
    </div>
  </popup-modal>

  <!-- ===== Hosting-config modal ===== -->
  <popup-modal ref="configModal">
    <h5 class="mb-1 d-flex align-items-center" style="gap:0.5rem">
      <font-awesome-icon :icon="['fas', 'cog']" style="color:#ECB109" />
      App-distribusjon
    </h5>
    <div class="cfg-desc">
      Global distribusjonskonfig — endringer tas i bruk uten restart. Klikk
      <font-awesome-icon :icon="['fas', 'info-circle']" class="apk-info-inline" /> for detaljer per seksjon.
    </div>

    <config-lock ref="cfgLock" :configured="cfgConfigured" :read-only="isLocalDev()"
                 read-only-text="Skrivebeskyttet — Backoffice kjører fra localhost mot produksjonens Play-utgiver.">
    <div class="apk-cfg-body">
    <!-- 1. Apper & lisensnøkler (per app) -->
    <div class="apk-modal-section">
      <span>Apper</span>
      <button class="apk-info-btn" :class="{ 'apk-info-btn--on': helpOpen.apps }"
              @click="toggleHelp('apps')" title="Vis/skjul hjelp">
        <font-awesome-icon :icon="['fas', 'info-circle']" />
      </button>
      <span class="badge ms-auto" :class="apkForm.apps.length ? 'bg-success' : 'bg-secondary'">
        {{ apkForm.apps.length ? apkForm.apps.length + (apkForm.apps.length > 1 ? ' apper' : ' app') : 'Ingen apper' }}
      </span>
    </div>
    <div v-if="helpOpen.apps" class="apk-help">
      Én rad per app vi distribuerer (pakkenavnet må matche APK-en). Rekkefølgen er nå enkel:
      <div class="apk-help-steps">
        <div class="apk-help-step">Legg til pakkenavnet</div>
        <div class="apk-help-step">Last opp signert APK</div>
        <div class="apk-help-step">«Aktiver» — laster opp til Play Console</div>
        <div class="apk-help-step">Rull ut trinnvis (se Trinnvis utrulling)</div>
      </div>
      <div class="mt-2">
        <strong>Lisensnøkkelen brukes ikke lenger.</strong> Den hørte til den utgåtte self-hosted-modellen,
        der enhetene lastet ned fra oss og vi verifiserte Plays nedlastings-JWT. Google Play sluttet å sende
        den cookien i juli 2026, sjekken ble fjernet, og med Google-hosting laster enhetene uansett ned fra
        Play. Feltet står igjen for eksisterende oppsett — en blank nøkkel blokkerer ingenting.
      </div>
    </div>

    <div v-for="(app, i) in apkForm.apps" :key="i" class="apk-app-row">
      <div class="d-flex align-items-center mb-2" style="gap:0.5rem">
        <button class="apk-app-remove" title="Fjern app" @click="removeApp(i)">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
        <input class="form-control form-control-sm" style="max-width:220px; font-family:monospace"
               v-model="app.packageName" placeholder="com.cagista.tastypos" />
        <input class="form-control form-control-sm" v-model="app.displayName" placeholder="Visningsnavn (f.eks. TastyPOS)" />
        <span v-if="(app.licensePublicKeyBase64 || '').trim()" class="badge bg-body-tertiary text-body border"
              title="Lisensnøkkel fra self-hosted-tiden — brukes ikke lenger, men skader ikke å la stå">
          Lisensnøkkel (legacy)
        </span>
      </div>
      <details class="apk-legacy-key">
        <summary>Lisensnøkkel (legacy — brukes ikke)</summary>
        <textarea class="form-control mt-1" rows="2" v-model="app.licensePublicKeyBase64"
                  placeholder="Tom er helt greit. Hørte til self-hosted-modellen; blokkerer ingenting."
                  style="font-family:monospace; font-size:0.78rem"></textarea>
      </details>
    </div>
    <div v-if="!apkForm.apps.length" class="apk-field-hint mb-2">Ingen apper lagt til ennå.</div>
    <button class="bo_btn_secondary" @click="addApp">
      <font-awesome-icon :icon="['fas', 'plus']" /> Legg til app
    </button>

    <div class="mt-3">
      <label class="form-label mb-1 mdm-label">Public API base-URL</label>
      <input class="form-control" type="text" v-model="apkForm.publicApiBaseUrl" placeholder="https://cagista.services" />
      <span class="apk-field-hint">Vår egen host for <code>/api/public/apk/**</code> — alltid PROD, må være
      https. Enhetene laster IKKE ned herfra lenger (det gjør de fra Play); den betjener versjonsoppslaget
      og den utgåtte self-hosted-nedlastingen.</span>
    </div>

    <!-- 2. Lagring -->
    <div class="apk-modal-section">
      <span>Lagring</span>
      <button class="apk-info-btn" :class="{ 'apk-info-btn--on': helpOpen.storage }"
              @click="toggleHelp('storage')" title="Vis/skjul hjelp">
        <font-awesome-icon :icon="['fas', 'info-circle']" />
      </button>
      <span class="badge ms-auto bg-body-tertiary text-body border">{{ apkForm.s3Bucket ? 'S3' : 'Lokal mappe' }}</span>
    </div>
    <div v-if="helpOpen.storage" class="apk-help">
      Her ligger <strong>arkivet</strong>: hver release versjonert for seg, kilden «Aktiver» laster opp til
      Play fra, og revisjonssporet (SHA-256) over hva som faktisk ble sendt ut.
      <div class="mt-2">
        <strong>Men det er ikke et rulle-tilbake-lager.</strong> En gammel APK herfra kan ikke installeres
        på en administrert enhet: Play nekter lavere version code, og enhetene installerer bare fra Play.
        Eneste vei tilbake er å bygge forrige kildekode på nytt med HØYERE version code.
      </div>
      <strong>Blank bucket</strong> → lokal mappe (dev-fallback). Key-prefix og presign-levetid gjelder kun S3.
      <div class="mt-2">
        Båndbredde er ikke lenger et argument her — enhetene laster ned fra Play, ikke fra oss. Presignert
        URL og 302-redirect gjelder bare den utgåtte self-hosted-veien.
      </div>
    </div>
    <div class="row g-2">
      <div class="col-7">
        <label class="form-label mb-1 mdm-label">Privat S3-bucket</label>
        <input class="form-control" type="text" v-model="apkForm.s3Bucket" placeholder="cagista-apks" />
      </div>
      <div class="col-5">
        <label class="form-label mb-1 mdm-label">Key-prefix</label>
        <input class="form-control" type="text" v-model="apkForm.s3KeyPrefix" placeholder="apks/" />
      </div>
      <div class="col-5">
        <label class="form-label mb-1 mdm-label">Presign-levetid (sek)</label>
        <input class="form-control" type="number" min="60" max="86400" v-model.number="apkForm.presignTtlSeconds" placeholder="600" />
      </div>
      <div class="col-7">
        <label class="form-label mb-1 mdm-label">Lokal mappe (fallback uten S3)</label>
        <input class="form-control" type="text" v-model="apkForm.folderPath" placeholder="/opt/cagistaservices/apks" />
      </div>
    </div>

    <!-- 3. Play-publisering -->
    <div class="apk-modal-section">
      <span>Play-publisering — ett-klikks «Aktiver»</span>
      <button class="apk-info-btn" :class="{ 'apk-info-btn--on': helpOpen.publish }"
              @click="toggleHelp('publish')" title="Vis/skjul hjelp">
        <font-awesome-icon :icon="['fas', 'info-circle']" />
      </button>
      <span class="badge ms-auto" :class="apkForm.publisherConfigured ? 'bg-success' : 'bg-secondary'">
        {{ apkForm.publisherConfigured ? 'Konfigurert ✓' : 'Ikke satt — ingen enhet kan installere' }}
      </span>
    </div>
    <div v-if="helpOpen.publish" class="apk-help">
      En <strong>DEDIKERT</strong> service-konto med kun release-rettighet (aldri AMAPI-kontoen). Med den satt
      laster «Aktiver» APK-en opp til Play Console (production) — og siden enhetene installerer fra Play, er
      dette hele distribusjonen. Uten den blir «Aktiver» bare en lokal peker og <strong>ingen enhet kan
      installere versjonen</strong>. Samme konto brukes til Play Integrity, som er det som lar en terminal
      logge seg selv inn etter en tvungen oppdatering. Lagres kryptert — vises aldri igjen.
      Tom = behold eksisterende.
    </div>
    <div>
      <label class="form-label mb-1 mdm-label">Publisher service-konto (JSON)</label>
      <textarea class="form-control" rows="2" v-model="publisherJson"
                placeholder="Play Console → Setup → API access. Tom = behold eksisterende."
                style="font-family:monospace; font-size:0.78rem"></textarea>
    </div>
    </div><!-- /apk-cfg-body -->

    <div class="d-flex justify-content-end align-items-center gap-3 mt-3">
      <span v-if="message" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
      <button class="bo_btn" @click="save" :disabled="saving">{{ saving ? 'Lagrer…' : 'Lagre' }}</button>
    </div>
    </config-lock>

    <div class="d-flex justify-content-start mt-2">
      <button class="bo_btn_secondary" @click="$refs.configModal.close()" :disabled="saving">Lukk</button>
    </div>
  </popup-modal>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import PopupModal from "@/components/other/PopupModal";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import { isLocalDev, refuseIfLocalDev } from "@/util/MdmLocalGuard";
import ConfigLock from "@/components/other/ConfigLock";
import AppRollout from "@/components/mdm/AppRollout";
import MdmService from "@/services/system/MdmService";

// Fallback display names for the overview table only (the managed list in the config is the source of
// truth for the upload dropdown + per-app license keys).
const APP_NAMES = {
  "com.cagista.tastypos": "TastyPOS",
  "com.cagista.tastykiosk": "TastyKIOSK",
  "com.cagista.tastykds": "TastyKDS",
  "com.cagista.tastyods": "TastyODS",
  "com.cagista.backoffice": "Backoffice"
};

export default {
  name: "MdmApps",
  components: { LoadingSpinner, PopupModal, ConfirmDialogue, ConfigLock, AppRollout },
  data() {
    return {
      loading: true,
      apkForm: { apps: [], folderPath: "", s3Bucket: "", s3KeyPrefix: "apks/", presignTtlSeconds: 600, publicApiBaseUrl: "https://cagista.services", publisherConfigured: false },
      publisherJson: "",
      // Per-section help panels in the config modal — collapsed by default (progressive disclosure).
      helpOpen: { apps: false, storage: false, publish: false },
      definitionBusy: null,
      availabilityBusy: null,
      activateBusy: null,   // "<packageName>:<versionCode>" while Play is being published to
      saving: false,
      message: "",
      error: false,

      // Hosted-APK overview
      apks: [],
      storedIn: "",
      apksLoading: false,
      apksError: "",
      apksNotice: "",
      copiedPkg: null,

      uploadPackageName: "com.cagista.tastypos",
      uploadFile: null,
      uploading: false,
      uploadPct: 0,
      uploadMessage: "",
      uploadError: false,
      uploadResult: null
    };
  },
  computed: {
    // Package names for the staged-rollout panel — from the configured apps, falling back to whatever
    // has actually been uploaded, so the panel is usable before the app list is filled in.
    rolloutPackages() {
      const configured = this.apkForm.apps.map((a) => (a.packageName || "").trim()).filter(Boolean);
      const uploaded = this.apks.map((a) => (a.packageName || "").trim()).filter(Boolean);
      return [...new Set([...configured, ...uploaded])];
    },
    // How many configured apps have a license key — drives the status badges.
    configuredAppCount() {
      return this.apkForm.apps.filter((a) => (a.licensePublicKeyBase64 || "").trim()).length;
    },
    // Already configured (→ App-distribusjon modal starts locked): a license key, an S3 bucket, or the
    // Play publisher account is set. A fresh/empty config opens editable.
    cfgConfigured() {
      return this.configuredAppCount > 0 || !!this.apkForm.s3Bucket || this.apkForm.publisherConfigured;
    }
  },
  mounted() {
    this.loadConfig();
    this.loadApks();
  },
  methods: {
    // App display name: the APK's own manifest label first, then the configured app's display name,
    // then the known-package fallback map, then the raw package name.
    appName(a) {
      const cfg = this.apkForm.apps.find((x) => x.packageName === a.packageName);
      return a.label || (cfg && cfg.displayName) || APP_NAMES[a.packageName] || a.packageName;
    },
    addApp() {
      this.apkForm.apps.push({ packageName: "", displayName: "", licensePublicKeyBase64: "" });
    },
    removeApp(i) {
      this.apkForm.apps.splice(i, 1);
    },
    // From the upload modal's "no apps configured" notice: jump straight to the config modal.
    openConfigFromUpload() {
      this.$refs.uploadModal.close();
      this.openConfigModal();
    },
    shortSha(sha) {
      return sha.length > 16 ? sha.substring(0, 16) + "…" : sha;
    },
    copySha(a) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(a.sha256Base64).then(() => {
          this.copiedPkg = a.packageName + ":" + a.versionCode;
          setTimeout(() => { this.copiedPkg = null; }, 1500);
        }).catch(() => {});
      }
    },
    // One-click release: publishes the Play release (when the publisher account is configured) AND makes
    // this version the one the stable download URL serves. Re-activating an older one = local rollback.
    // Is this package actually distributed to OUR enterprise in managed Google Play? A 404 means devices can never
    // license/install it (not targeted to the org, or a freshly published private app still propagating) — which
    // looks identical to a hosting bug (the licensed download arrives with "no cookies").
    checkAvailability(a) {
      this.availabilityBusy = a.packageName;
      this.apksError = "";
      this.apksNotice = "";
      MdmService.checkAppAvailability(a.packageName).then(
        (response) => {
          this.availabilityBusy = null;
          const d = response.data || {};
          if (d.available) {
            const title = (d.app && d.app.title) || a.packageName;
            this.apksNotice = `✓ ${a.packageName} er tilgjengelig for vår enterprise i managed Google Play (${title}).`;
          } else {
            this.apksError = `✗ ${a.packageName}: ${d.message || "ikke tilgjengelig for enterprise i managed Google Play."}`;
          }
        },
        (error) => {
          this.availabilityBusy = null;
          this.apksError = this.errMsg(error, "Kunne ikke sjekke tilgjengelighet i managed Google Play.");
        }
      );
    },
    isLocalDev,
    // «Aktiver» is the single most far-reaching button in MDM: it publishes the release in Play, and every
    // till installs it. Nothing on this page may run from a developer's browser.
    refuseIfLocalDev() {
      return refuseIfLocalDev(this.$refs.confirmDialog,
        "Appversjoner kan leses herfra, men ikke lastes opp, aktiveres eller slettes: «Aktiver» publiserer " +
        "releasen i Play Console og hele flåten installerer den.");
    },
    async activateApk(a) {
      if (await this.refuseIfLocalDev()) return;
      const playPart = this.apkForm.publisherConfigured
        ? `<br/><strong>Ett klikk — hele løypa:</strong> publiserer releasen i Play Console (production) og ` +
          `aktiverer nedlastings-URL-en samtidig.`
        : `<br/><strong>NB:</strong> Play-publisering er ikke konfigurert, så dette blir KUN en lokal ` +
          `aktivering — Play får ingenting, og ingen enhet vil kunne installere versjonen. Sett opp ` +
          `publisher-service-kontoen, eller last APK-en opp manuelt i Play Console.`;
      const ok = await this.$refs.confirmDialog.show({
        title: "Aktiver versjon",
        message: `Aktivere <strong>${this.appName(a)}</strong> version code <strong>${a.versionCode}</strong>` +
          (a.versionName ? ` (${a.versionName})` : "") + `?` + playPart,
        okButton: "Aktiver", cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.apksNotice = "";
      this.apksError = "";
      // Uploading the APK to Play takes tens of seconds. Without a busy state the button looked
      // untouched the whole time, so there was nothing to tell a slow upload from a click that missed.
      this.activateBusy = a.packageName + ":" + a.versionCode;
      MdmService.activateApk(a.packageName, a.versionCode).then(
        (response) => {
          const r = response.data || {};
          this.activateBusy = null;
          this.loadApks();   // clears messages synchronously — set the notice after
          this.apksNotice = "Version code " + a.versionCode + " aktivert ✓" +
            (r.playMessage ? " — " + r.playMessage : "");
        },
        (error) => {
          this.activateBusy = null;
          this.apksError = this.errMsg(error, "Aktivering feilet.");
        }
      );
    },
    // Download the Play definition file for a hosted version — generated server-side from the hosted
    // bytes, so it always matches exactly what devices will download.
    downloadDefinition(a) {
      const key = a.packageName + ":" + a.versionCode;
      this.definitionBusy = key;
      MdmService.downloadApkDefinition(a.packageName, a.versionCode).then(
        (response) => {
          this.definitionBusy = null;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/json" }));
          const link = document.createElement("a");
          link.href = url;
          link.download = a.packageName + "-" + a.versionCode + ".definition.json";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
        (error) => {
          console.log(error);
          this.definitionBusy = null;
          this.apksError = "Kunne ikke generere definition-filen.";
        }
      );
    },
    async deleteApk(a) {
      if (await this.refuseIfLocalDev()) return;
      const ok = await this.$refs.confirmDialog.show({
        title: "Slett versjon",
        message: `Slette <strong>${this.appName(a)}</strong> version code <strong>${a.versionCode}</strong>` +
          (a.versionName ? ` (${a.versionName})` : "") +
          ` (${this.formatBytes(a.bytes)})?<br/>Filen fjernes permanent fra lagringen.`,
        okButton: "Slett", cancelButton: "Avbryt"
      });
      if (!ok) return;
      MdmService.deleteApk(a.packageName, a.versionCode).then(
        () => { this.loadApks(); },
        (error) => { this.apksError = this.errMsg(error, "Sletting feilet."); }
      );
    },
    // Keep uploadPackageName pointing at a configured app (defaults to the first) — the dropdown only
    // offers apps that exist in the config, so a stale value would leave nothing selected.
    ensureValidUploadPackage() {
      const apps = this.apkForm.apps;
      if (apps.length && !apps.some((x) => x.packageName === this.uploadPackageName)) {
        this.uploadPackageName = apps[0].packageName;
      }
      if (!apps.length) {
        this.uploadPackageName = "";
      }
    },
    async openUploadModal() {
      if (await this.refuseIfLocalDev()) return;
      this.uploadMessage = "";
      this.uploadError = false;
      this.uploadResult = null;
      this.uploadFile = null;
      this.ensureValidUploadPackage();
      this.$refs.uploadModal.open();
      // The file input lives inside the modal; clear it once rendered.
      this.$nextTick(() => {
        if (this.$refs.apkFile) {
          this.$refs.apkFile.value = "";
        }
      });
    },
    openConfigModal() {
      this.message = "";
      this.error = false;
      this.$refs.configModal.open();
    },
    toggleHelp(key) {
      this.helpOpen[key] = !this.helpOpen[key];
    },
    loadApks() {
      this.apksLoading = true;
      this.apksError = "";
      this.apksNotice = "";
      MdmService.listApks().then(
        (response) => {
          const data = response.data || {};
          this.apks = Array.isArray(data.apks) ? data.apks : [];
          this.storedIn = data.storedIn || "";
          this.apksLoading = false;
        },
        (error) => {
          this.apksLoading = false;
          this.apksError = this.errMsg(error, "Kunne ikke hente APK-listen.");
        }
      );
    },
    loadConfig() {
      MdmService.getApkConfig().then(
        (response) => {
          const a = response.data || {};
          this.apkForm.apps = Array.isArray(a.apps)
            ? a.apps.map((x) => ({
                packageName: x.packageName || "",
                displayName: x.displayName || "",
                licensePublicKeyBase64: x.licensePublicKeyBase64 || ""
              }))
            : [];
          this.apkForm.folderPath = a.folderPath || "";
          this.apkForm.s3Bucket = a.s3Bucket || "";
          this.apkForm.s3KeyPrefix = a.s3KeyPrefix || "apks/";
          this.apkForm.presignTtlSeconds = a.presignTtlSeconds || 600;
          this.apkForm.publicApiBaseUrl = a.publicApiBaseUrl || "https://cagista.services";
          this.apkForm.publisherConfigured = a.publisherConfigured === true;
          this.ensureValidUploadPackage();
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
          this.apksError = this.errMsg(error, "Kunne ikke hente konfigurasjon.");
        }
      );
    },
    async save() {
      if (await this.refuseIfLocalDev()) return;
      this.saving = true;
      this.message = "";
      this.error = false;

      // The publisher key is pasted as formatted JSON; minify to one line (blank = keep existing).
      let publisherCredentialsJson = this.publisherJson.trim();
      if (publisherCredentialsJson) {
        try {
          publisherCredentialsJson = JSON.stringify(JSON.parse(publisherCredentialsJson));
        } catch (e) {
          this.saving = false;
          this.error = true;
          this.message = "Ugyldig JSON i Play publisher-nøkkelen.";
          return;
        }
      }

      MdmService.saveApkConfig({
        apps: this.apkForm.apps,
        folderPath: this.apkForm.folderPath,
        s3Bucket: this.apkForm.s3Bucket,
        s3KeyPrefix: this.apkForm.s3KeyPrefix,
        presignTtlSeconds: this.apkForm.presignTtlSeconds,
        publicApiBaseUrl: this.apkForm.publicApiBaseUrl,
        publisherCredentialsJson: publisherCredentialsJson
      }).then(
        (response) => {
          this.saving = false;
          this.error = false;
          this.message = "Lagret ✓";
          this.publisherJson = "";   // don't keep the secret in the DOM
          const a = response.data || {};
          this.apkForm.publisherConfigured = a.publisherConfigured === true;
          // The server normalizes (dedup / drops invalid packageNames) — mirror that back into the form.
          if (Array.isArray(a.apps)) {
            this.apkForm.apps = a.apps.map((x) => ({
              packageName: x.packageName || "",
              displayName: x.displayName || "",
              licensePublicKeyBase64: x.licensePublicKeyBase64 || ""
            }));
          }
          this.ensureValidUploadPackage();
          if (this.$refs.cfgLock) this.$refs.cfgLock.relock();   // re-lock after a successful save
          this.loadApks(); // bucket/prefix may have changed — refresh the overview
        },
        (error) => {
          this.saving = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke lagre konfigurasjon.");
        }
      );
    },
    onApkFileChange(e) {
      this.uploadFile = e.target.files && e.target.files.length ? e.target.files[0] : null;
      this.uploadResult = null;
      this.uploadMessage = "";
      this.uploadError = false;
    },
    async uploadApk() {
      if (await this.refuseIfLocalDev()) return;
      if (!this.uploadFile) {
        return;
      }
      this.uploading = true;
      this.uploadPct = 0;
      this.uploadMessage = "";
      this.uploadError = false;
      this.uploadResult = null;

      const formData = new FormData();
      formData.append("packageName", this.uploadPackageName);
      formData.append("file", this.uploadFile);

      MdmService.uploadApk(formData, (evt) => {
        if (evt.total) {
          this.uploadPct = Math.round((evt.loaded / evt.total) * 100);
        }
      }).then(
        (response) => {
          this.uploading = false;
          this.uploadResult = response.data || null;
          this.uploadMessage = "Lastet opp ✓";
          this.uploadFile = null;
          if (this.$refs.apkFile) {
            this.$refs.apkFile.value = "";
          }
          this.loadApks(); // the overview should immediately show the new upload
        },
        (error) => {
          this.uploading = false;
          this.uploadError = true;
          this.uploadMessage = this.errMsg(error, "Opplasting feilet.");
        }
      );
    },
    formatBytes(b) {
      return b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.round(b / 1024) + " kB";
    },
    // Norwegian timestamp dd.mm.yyyy hh:mm from epoch millis.
    formatTime(ms) {
      if (!ms) return "—";
      const dt = new Date(ms);
      const p = (n) => String(n).padStart(2, "0");
      return p(dt.getDate()) + "." + p(dt.getMonth() + 1) + "." + dt.getFullYear() +
             " " + p(dt.getHours()) + ":" + p(dt.getMinutes());
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
/* Localhost-against-production notice, same as the MDM device and policy pages */
.mdm-devbanner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 680px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-style: normal;
  color: var(--bo-brand-ink);
  background: var(--bo-brand-tint);
  border: 1px solid #ffd591;
}

.apk-legacy-key > summary {
  cursor: pointer;
  font-size: 0.78rem;
  color: #adb5bd;
}

.mdm-back {
  border: none;
  background: none;
  color: var(--bo-brand-ink);
  padding: 0;
  font-size: 0.9rem;
  cursor: pointer;
}
.mdm-back:hover {
  text-decoration: underline;
}
.mdm-label {
  font-size: 0.85rem;
  color: var(--bo-muted);
}
.cfg-card {
  border: 1px solid #e6e6e6;
  border-left: 3px solid #ECB109;
  border-radius: 10px;
  background: var(--bo-surface);
  padding: 1rem 1.25rem 1.25rem;
  margin-top: 1.25rem;
}
.cfg-card-header {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.cfg-card-header .cfg-icon {
  color: #ECB109;
  font-size: 1.05rem;
  width: 1.3rem;
  text-align: center;
}
.cfg-card-header h5 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
}
.cfg-card-header .badge {
  font-weight: 500;
}
.cfg-desc {
  font-style: italic;
  color: var(--bo-muted);
  font-size: 0.85rem;
  margin-top: 0.4rem;
}
/* Grouped sections inside the config modal (same idiom as Mdm.vue's mdm-details__section). */
.apk-modal-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.88rem;
  color: var(--bo-ink);
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  padding-bottom: 3px;
  border-bottom: 1px solid var(--bo-line);
}
.apk-modal-section .badge {
  font-weight: 500;
}
.apk-field-hint {
  color: var(--bo-muted);
  font-size: 0.76rem;
}
/* Scrollable modal body — header + footer stay put, the sections scroll if the form gets long. */
.apk-cfg-body {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
  margin-top: 0.25rem;
}
/* Section info toggle (ⓘ) — sits between the section title and its status badge; reveals help on click. */
.apk-info-btn {
  border: none;
  background: none;
  color: #b8b8b8;
  cursor: pointer;
  padding: 0;
  font-size: 0.92rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  transition: color 0.15s ease;
}
.apk-info-btn:hover,
.apk-info-btn--on {
  color: #ECB109;
}
.apk-info-inline {
  color: #ECB109;
}
/* On-demand help panel revealed by a section's ⓘ toggle. */
.apk-help {
  background: var(--bo-brand-tint);
  border: 1px solid #f2e2a8;
  border-left: 3px solid #ECB109;
  border-radius: 6px;
  padding: 0.55rem 0.7rem;
  margin-bottom: 0.65rem;
  color: #5a4a20;
  font-size: 0.8rem;
  line-height: 1.45;
}
/* Numbered steps built from DIVs (NOT ol/li) so the global list reset can't touch them at all:
   each row is a plain block div; the number is a CSS counter rendered as generated content. */
.apk-help-steps {
  counter-reset: apk-step;
  margin: 0.4rem 0 0.45rem;
}
.apk-help-step {
  display: block;
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.2rem;
}
.apk-help-step::before {
  counter-increment: apk-step;
  content: counter(apk-step) ".";
  position: absolute;
  left: 0;
  top: 0;
  font-weight: 600;
  color: var(--bo-brand-ink);
}
/* Give this page's modals a bit more width than PopupModal's 480px default.
   (:deep(.window) — the .popup-modal root itself carries this page's scope attr.) */
:deep(.window) {
  max-width: 560px;
  width: 100%;
}
.apps-meta {
  color: var(--bo-muted);
  font-size: 0.85rem;
  white-space: nowrap;
}
.apk-empty {
  text-align: center;
  padding: 1.75rem 0 1rem;
}
.apk-empty__icon {
  font-size: 2rem;
  color: #d8d8d8;
}
.apk-footnote {
  color: var(--bo-muted);
  font-size: 0.78rem;
}
.apk-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--bo-line);
  background: var(--bo-surface);
  color: var(--bo-ink-2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.apk-refresh:hover:not(:disabled) {
  background: var(--bo-surface-2);
  transform: translateY(-1px);
}
.apk-table {
  font-size: 0.86rem;
  margin-bottom: 0.25rem;
}
.apk-pkg {
  font-size: 0.78rem;
  color: var(--bo-muted);
}
.apk-sha {
  cursor: pointer;
  font-size: 0.78rem;
}
.apk-sha:hover {
  color: var(--bo-brand-ink);
}
/* Per-app row in the config modal — a light card holding packageName + display name + its license key. */
.apk-app-row {
  border: 1px solid #ececec;
  border-radius: 8px;
  background: var(--bo-surface-2);
  padding: 0.6rem 0.7rem;
  margin-bottom: 0.6rem;
}
.apk-app-row .badge {
  font-weight: 500;
  white-space: nowrap;
}
/* Remove-app button leads the row on the LEFT (destructive-action-on-left convention). */
.apk-app-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border: 1px solid #e3b1b1;
  background: var(--bo-surface);
  color: #b33;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.15s ease;
}
.apk-app-remove:hover {
  background: var(--bo-bad-bg);
  transform: translateY(-1px);
}
/* Compact row-action buttons (same idiom as Mdm.vue's mdm-icon-btn; delete leads the row on the LEFT). */
.apk-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid var(--bo-line);
  background: var(--bo-surface);
  color: var(--bo-ink-2);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: all 0.15s ease;
}
.apk-icon-btn:hover {
  background: var(--bo-surface-2);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.apk-icon-btn--danger {
  color: var(--bo-alert-ink);
  border-color: #e7c3bd;
}
.apk-icon-btn--danger:hover {
  background: var(--bo-bad-bg);
}
.fa-fade-busy {
  opacity: 0.35;
}
/* Publishing to Play takes tens of seconds — the button has to look like it is working the whole time. */
.apk-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  margin-right: 6px;
  border: 2px solid rgba(138, 104, 0, 0.25);
  border-top-color: #8A6800;
  border-radius: 50%;
  animation: apk-spin 0.7s linear infinite;
}
@keyframes apk-spin {
  to { transform: rotate(360deg); }
}
.apk-icon-btn:disabled {
  cursor: default;
  opacity: 0.75;
}
.apk-activate {
  color: var(--bo-brand-ink);
  border-color: #ECB109;
}
.apk-activate:hover {
  background: var(--bo-brand-tint);
}
.apk-col-delete {
  width: 1%;
  white-space: nowrap;
  padding-left: 6px;
  padding-right: 6px;
}
.apk-col-delete__lock {
  color: #cfcfcf;
  font-size: 0.8rem;
  padding: 0 8px;
}
.upload-progress {
  height: 6px;
  max-width: 420px;
  background: var(--bo-line);
  border-radius: 3px;
  overflow: hidden;
}
.upload-progress > div {
  height: 100%;
  background: #ECB109;
  transition: width 0.2s ease;
}
</style>
